import React from 'react';
import { Provider } from 'react-redux';

import type { Template, TemplateCategory } from 'js/src/client';
import type { ContentfulPageListingGroup } from 'js/src/libs/contentful-tools';
import { seedContentfulPageData } from 'js/src/libs/contentful-tools';
import PublicPagesTrim from 'js/src/components/common/public-pages/PublicPagesTrim';
import { Toast } from 'js/src/components/common/Toast';
import { RequestErrorModal } from 'js/src/components/common/RequestErrorModal';
import PublicThemesPage from 'js/src/components/public-pages/themes-page/PublicThemesPage';
import {
    APIRequestStatus,
    setTemplateCategoriesListRequestStatus,
    setTemplatesListRequestStatus,
} from 'js/src/data/templates/slices/loadingInfoSlice';
import { storeTemplateCategories, storeTemplates } from 'js/src/data/templates/templates-actions';
import { getRootReducer } from 'js/src/entry-points/root-reducers/themes-page-reducers';
import { createReduxStore } from 'js/src/libs/redux-utils';

export type ThemesPageProps = {
    templates: Template[];
    categories: TemplateCategory[];
    openTemplateId: string | null;
    contentfulPageListings: ContentfulPageListingGroup[];
};

export function prepareThemesPage(props: ThemesPageProps) {
    seedContentfulPageData(props.contentfulPageListings ?? []);
    return createThemesPageStore(props);
}

export function createThemesPageStore(props: ThemesPageProps) {
    const store = createReduxStore(getRootReducer());
    if (props.templates.length > 0) {
        store.dispatch(storeTemplates({ templates: props.templates }));
        // `data` is what marks each template's own retrieve request fetched, exactly as
        // loadTemplates does - without it the page refetches them one at a time.
        store.dispatch(
            setTemplatesListRequestStatus({
                status: APIRequestStatus.FETCHED,
                data: props.templates,
            }),
        );
    }
    if (props.categories.length > 0) {
        store.dispatch(storeTemplateCategories({ categories: props.categories }));
        store.dispatch(
            setTemplateCategoriesListRequestStatus({ status: APIRequestStatus.FETCHED }),
        );
    }
    return store;
}

export const ThemesPageApp: React.FC<{
    props: ThemesPageProps;
    store: ReturnType<typeof createThemesPageStore>;
}> = ({ props, store }) => (
    <Provider store={store}>
        <PublicPagesTrim headerBackgroundColor={'#FFF'} floatingCookieBanner>
            <PublicThemesPage initialTemplateId={props.openTemplateId} />
        </PublicPagesTrim>
        <Toast.Component />
        <RequestErrorModal />
    </Provider>
);
